import { Graphics, type Sprite } from 'pixi.js';
import { FULL_SUMMONS } from '../../data/full/summons';
import type { SummonDefinition } from '../../data/types';
import { lerp } from '../../shared/scalar';
import { HEAVY_HALF_LENGTH, summonOf } from '../../sim/full/summons';
import type { PlayerState, SimEvent, SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { add, hide, setTint } from '../util';
import { Pen } from './pen';

const TAU = Math.PI * 2;
const PLATES = 9;
const MASK_PLATES = [1, 4, 7];
const LAYOUT_RADIUS = 64;
const MAX_LEVEL = 3;
const WOOD = { light: 0xb07a42, mid: 0x8a5a2b, dark: 0x5c3816, grain: 0xe8c08a, ring: 0x9a6a38 };
const INK = 0x100804;
const STEEL = 0x2b1609;
const PIT = 0x2c1309;
const LOG_WIDTH = 18;
const STICK_LENGTH = 30;
const FLARE_BEATS = 2;

interface Flare {
  x: number;
  y: number;
  start: number;
}

export function createCampfire(ctx: RenderContext): Family {
  const { layers, textures: t } = ctx;
  const pool = add(layers.glow, t.halo);
  const glow = add(layers.glow, t.halo);
  const cast = new Graphics();
  layers.glow.addChild(cast);
  const body = new Graphics();
  layers.traps.addChild(body);
  const sparks = new Graphics();
  layers.fx.addChild(sparks);
  const pile = new Graphics();
  layers.traps.addChild(pile);
  const carried = new Graphics();
  layers.weapons.addChild(carried);
  const castPen = new Pen(cast);
  const bodyPen = new Pen(body);
  const pilePen = new Pen(pile);
  const carriedPen = new Pen(carried);
  const flares: Flare[] = [];
  const all: (Graphics | Sprite)[] = [pool, glow, cast, body, sparks, pile, carried];

  function hideAll(): void {
    hide(pool, glow);
    for (const graphics of [cast, body, sparks, pile, carried]) {
      graphics.clear();
    }
  }

  return {
    update(state: SimState, alpha: number, frame: Frame): void {
      const definition = fireOf(state);
      const site = definition?.sites[0];
      const source = definition?.sources[0];
      if (definition === undefined || site === undefined || source === undefined) {
        hideAll();
        return;
      }
      const summon = summonOf(state);
      const level = Math.min(MAX_LEVEL, summon?.burnt[0] ?? 0);
      const left = source.items - (summon?.taken[0] ?? 0);
      const motion = motionOf(frame);

      drawFire(site, level, frame, motion);
      drawSparks(site, level, frame, motion);
      drawPile(source, left, frame, motion);
      carriedPen.clear();
      for (const heavy of summon?.heavy ?? []) {
        const x = lerp(heavy.prevX, heavy.x, alpha);
        const y = lerp(heavy.prevY, heavy.y, alpha);
        drawLog(carriedPen, x, y, Math.atan2(heavy.ay, heavy.ax), 2 * HEAVY_HALF_LENGTH - 34);
      }
      for (const player of state.players) {
        if (player.offering !== undefined) {
          drawStick(carriedPen, player, alpha);
        }
      }
    },
    onEvent(event: SimEvent, state: SimState, frame: Frame): void {
      if (event.type === 'offeringBurnt' && fireOf(state) !== undefined) {
        flares.push({ x: event.x, y: event.y, start: frame.now });
      }
    },
    reset(): void {
      flares.length = 0;
    },
    destroy(): void {
      for (const shown of all) {
        shown.destroy();
      }
    },
  };

  function drawFire(
    site: SummonDefinition['sites'][number],
    level: number,
    frame: Frame,
    motion: Motion,
  ): void {
    const { palette } = frame;
    const intensity = [0.45, 0.7, 0.85, 1][level] ?? 1;
    const scale = site.radius / LAYOUT_RADIUS;
    const radius = LAYOUT_RADIUS;

    pool.visible = true;
    setTint(pool, palette.tank);
    pool.position.set(site.x, site.y);
    pool.scale.set((site.radius * 2.6) / t.halo.radius);
    pool.alpha = 0.32 * intensity * frame.light.haloAlpha;

    castPen.clear().translate(site.x, site.y).scale(scale);
    for (let plate = 0; plate < PLATES; plate++) {
      const angle = (plate * TAU) / PLATES - Math.PI / 2;
      const masked = MASK_PLATES.indexOf(plate);
      castPen
        .save()
        .rotate(angle + Math.PI / 2)
        .translate(0, -(radius + 30));
      if (masked !== -1 && masked < level) {
        stencilPath(cast, 17 + 2 * motion.pulse);
        cast.fill({ color: palette.noyau, alpha: 0.85 + 0.15 * motion.pulse });
        stencilHoles(cast, 17 + 2 * motion.pulse);
        cast.cut();
      } else {
        const size = 26 + 5 * level;
        const flicker = motion.calm ? 1 : 0.85 + 0.15 * Math.sin(motion.beats * 0.6 + plate * 2.1);
        castPen.translate(0, size * 0.42);
        flamePath(cast, size * 0.34, size);
        cast.fill({ color: palette.noyau, alpha: 0.6 * intensity * flicker });
        hookPath(cast, size * 0.34, size);
        cast.cut();
      }
      castPen.restore();
      const gap = angle + Math.PI / PLATES;
      const inner = radius;
      const outer = radius + 55;
      cast
        .moveTo(Math.cos(gap - 0.05) * inner, Math.sin(gap - 0.05) * inner)
        .lineTo(Math.cos(gap - 0.11) * outer, Math.sin(gap - 0.11) * outer)
        .lineTo(Math.cos(gap + 0.11) * outer, Math.sin(gap + 0.11) * outer)
        .lineTo(Math.cos(gap + 0.05) * inner, Math.sin(gap + 0.05) * inner)
        .closePath()
        .fill({ color: palette.tank, alpha: 0.2 * intensity });
    }

    bodyPen.clear().translate(site.x, site.y).scale(scale);
    body.circle(0, 0, radius - 4).fill({ color: PIT });
    body.circle(0, 0, (radius - 4) * 0.55).fill({ color: 0x6e2d12, alpha: 0.5 + 0.4 * intensity });
    for (let ember = 0; ember < 26; ember++) {
      const angle = ember * 2.39996;
      const distance = (0.35 + ((ember * 37) % 55) / 100) * (radius - 4);
      body
        .circle(Math.cos(angle) * distance, Math.sin(angle) * distance, 1 + (ember % 3) * 0.6)
        .fill({ color: palette.tank, alpha: (0.3 + (ember % 4) * 0.12) * intensity });
    }
    const logs = 2 + level;
    for (let log = 0; log < logs; log++) {
      const length = (radius - 14) * 1.25;
      const width = (radius - 14) * 0.15;
      bodyPen.save().rotate((log * TAU) / logs / 2 + 0.3);
      body
        .roundRect(-length / 2, -width / 2, length, width, width / 2)
        .fill({ color: 0x2a140a })
        .stroke({ width: 2, color: INK })
        .moveTo(-length * 0.32, -width * 0.15)
        .lineTo(-length * 0.12, width * 0.12)
        .moveTo(length * 0.08, -width * 0.2)
        .lineTo(length * 0.3, width * 0.1)
        .stroke({ width: 1.4, color: palette.tank, alpha: 0.7 });
      bodyPen.restore();
    }
    drawSwirl(bodyPen, radius - 10, level, frame, motion);
    const half = (TAU / PLATES) * 0.3;
    for (let plate = 0; plate < PLATES; plate++) {
      const angle = (plate * TAU) / PLATES - Math.PI / 2;
      arcFrom(body, radius + 3, angle - half, angle + half)
        .arc(0, 0, radius - 4, angle + half, angle - half, true)
        .closePath()
        .fill({ color: STEEL })
        .stroke({ width: 2.6, color: 0x070302 });
      arcFrom(body, radius - 4, angle - half + 0.02, angle + half - 0.02).stroke({
        width: 1.6,
        color: palette.tank,
        alpha: 0.5 * intensity + 0.2 * motion.pulse,
      });
      arcFrom(body, radius + 3, angle - half + 0.03, angle + half - 0.03).stroke({
        width: 1,
        color: palette.texte,
        alpha: 0.3,
      });
    }

    const length = flameLength(level, motion) * scale * (radius - 10);
    glow.visible = true;
    setTint(glow, palette.noyau);
    glow.position.set(site.x, site.y);
    glow.scale.set((length * 1.4 + 6) / t.halo.radius);
    glow.alpha = (0.55 + 0.25 * motion.pulse) * frame.light.haloAlpha;
  }

  function drawSparks(
    site: SummonDefinition['sites'][number],
    level: number,
    frame: Frame,
    motion: Motion,
  ): void {
    const { palette } = frame;
    sparks.clear();
    const duration = FLARE_BEATS * frame.tempo.ticksPerBeat;
    let kept = 0;
    for (const flare of flares) {
      const progress = (frame.now - flare.start) / duration;
      if (progress >= 1) {
        continue;
      }
      flares[kept] = flare;
      kept += 1;
      if (progress >= 0) {
        sparks.circle(flare.x, flare.y, site.radius * (1 + 1.4 * progress)).stroke({
          width: 3 * (1 - progress) + 1,
          color: palette.tank,
          alpha: 0.75 * (1 - progress),
        });
      }
    }
    flares.length = kept;
    if (motion.calm) {
      return;
    }
    const count = [3, 12, 22, 34][level] ?? 34;
    const life = 1.6 * frame.tempo.ticksPerBeat;
    for (let spark = 0; spark < count; spark++) {
      const phase = (frame.now + spark * 7.3) / life;
      const cycle = Math.floor(phase);
      const age = phase - cycle;
      const seed = (spark * 92821 + cycle * 68917) % 1000;
      const angle = (seed / 1000) * TAU;
      const speed = 0.4 + ((seed * 7) % 100) / 100;
      const out = site.radius * (0.2 + age * speed * 1.3);
      const x = site.x + Math.cos(angle) * out + age * 14;
      const y = site.y + Math.sin(angle) * out - age * 30;
      sparks
        .circle(x, y, 1.6 * (1 - age * 0.5))
        .fill({ color: age < 0.4 ? palette.noyau : palette.tank, alpha: 1 - age });
    }
  }

  function drawPile(
    source: SummonDefinition['sources'][number],
    left: number,
    frame: Frame,
    motion: Motion,
  ): void {
    pilePen.clear().translate(source.x, source.y);
    if (left > 0) {
      const dashes = 18;
      const spin = motion.beats * 0.05;
      for (let dash = 0; dash < dashes; dash++) {
        const from = (dash * TAU) / dashes + spin;
        arcFrom(pile, source.radius + 16, from, from + (TAU / dashes) * 0.5);
        pile.stroke({ width: 2, color: frame.palette.or, alpha: 0.35 + 0.4 * motion.pulse });
      }
      pile.ellipse(4, 6, 40, 31).fill({ color: 0x000000, alpha: 0.35 });
    }
    pilePen.rotate(-0.1);
    const heap = [
      [-4, 11, -0.3, 0],
      [5, -5, -0.3, 0],
      [0, 2, 0.95, 1],
    ] as const;
    if (left === 0) {
      for (let chip = 0; chip < 14; chip++) {
        const x = (((chip * 37) % 60) - 30) * 0.9;
        const y = (((chip * 53) % 44) - 22) * 0.9;
        pilePen.save().translate(x, y).rotate(chip);
        pile.rect(-3, -1, 6, 2).fill({ color: WOOD.mid, alpha: 0.4 });
        pilePen.restore();
      }
    }
    for (const [x, y, angle, top] of heap.slice(0, left)) {
      drawLog(pilePen, x, y, angle, top === 1 ? 58 : 66, top === 1 ? 13 : 14);
    }
  }
}

interface Motion {
  calm: boolean;
  pulse: number;
  beats: number;
}

function motionOf(frame: Frame): Motion {
  const calm = frame.calm;
  return {
    calm,
    pulse: calm ? 0.4 : frame.pulse,
    beats: calm ? 0 : frame.now / frame.tempo.ticksPerBeat,
  };
}

function fireOf(state: SimState): SummonDefinition | undefined {
  return FULL_SUMMONS.find((summon) => summon.setId === state.setId && summon.look === 'fire');
}

// An arc around the origin that starts its own path: Pixi would otherwise join it to the path's origin.
function arcFrom(g: Graphics, radius: number, from: number, to: number): Graphics {
  return g.moveTo(Math.cos(from) * radius, Math.sin(from) * radius).arc(0, 0, radius, from, to);
}

function flameLength(level: number, motion: Motion): number {
  return ([0.3, 0.55, 0.75, 0.92][level] ?? 0.92) * (1 + 0.12 * motion.pulse);
}

function drawSwirl(pen: Pen, radius: number, level: number, frame: Frame, motion: Motion): void {
  const { palette } = frame;
  const g = pen.g;
  const tongues = [4, 5, 6, 7][level] ?? 7;
  const length = flameLength(level, motion) * radius;
  const swirl = motion.beats * 0.1;
  const layers = [
    { scale: 1, color: palette.mage, alpha: 0.9 },
    { scale: 0.86, color: palette.tank, alpha: 1 },
    { scale: 0.6, color: palette.noyau, alpha: 1 },
    { scale: 0.36, color: 0xfff3c4, alpha: 1 },
  ];
  for (const layer of layers) {
    for (let tongue = 0; tongue < tongues; tongue++) {
      const wobble = motion.calm ? 0 : 0.12 * Math.sin(motion.beats * 1.6 + tongue * 1.7);
      const angle = (tongue * TAU) / tongues + swirl + wobble + (layer.scale < 1 ? 0.12 : 0);
      const height =
        length *
        layer.scale *
        (0.9 + (motion.calm ? 0 : 0.1 * Math.sin(tongue * 2.3 + motion.beats * 2.3)));
      pen
        .save()
        .rotate(angle)
        .translate(0, -radius * 0.04);
      swirlTongue(g, height * 0.42, height);
      g.fill({ color: layer.color, alpha: layer.alpha });
      pen.restore();
    }
  }
  g.circle(0, 0, radius * (0.07 + 0.03 * motion.pulse)).fill({ color: 0xfff7da });
}

function swirlTongue(g: Graphics, w: number, h: number): void {
  g.moveTo(-w * 0.5, 0)
    .bezierCurveTo(-w * 1.3, -h * 0.35, -w * 0.9, -h * 0.85, w * 0.9, -h)
    .bezierCurveTo(-w * 0.1, -h * 0.82, -w * 0.15, -h * 0.4, w * 0.6, -h * 0.12)
    .quadraticCurveTo(w * 0.2, w * 0.5, -w * 0.5, 0)
    .closePath();
}

function flamePath(g: Graphics, w: number, h: number): void {
  g.moveTo(0, 0)
    .bezierCurveTo(-0.9 * w, -0.05 * h, -0.95 * w, -0.55 * h, -0.2 * w, -0.78 * h)
    .quadraticCurveTo(-0.1 * w, -0.95 * h, 0.35 * w, -h)
    .quadraticCurveTo(0, -0.82 * h, 0.4 * w, -0.62 * h)
    .bezierCurveTo(0.95 * w, -0.4 * h, 0.9 * w, -0.05 * h, 0, 0)
    .closePath();
}

function hookPath(g: Graphics, w: number, h: number): void {
  g.moveTo(0.12 * w, -0.3 * h)
    .bezierCurveTo(-0.45 * w, -0.32 * h, -0.4 * w, -0.62 * h, 0.05 * w, -0.6 * h)
    .bezierCurveTo(-0.18 * w, -0.5 * h, -0.1 * w, -0.38 * h, 0.12 * w, -0.3 * h)
    .closePath();
}

function stencilPath(g: Graphics, s: number): void {
  g.moveTo(-s, -0.62 * s)
    .quadraticCurveTo(0, -1.12 * s, s, -0.62 * s)
    .quadraticCurveTo(1.06 * s, 0.38 * s, 0, s)
    .quadraticCurveTo(-1.06 * s, 0.38 * s, -s, -0.62 * s)
    .closePath();
}

function stencilHoles(g: Graphics, s: number): void {
  for (const side of [-1, 1]) {
    g.moveTo(side * 0.62 * s, -0.28 * s)
      .lineTo(side * 0.14 * s, -0.08 * s)
      .lineTo(side * 0.16 * s, 0.06 * s)
      .lineTo(side * 0.6 * s, -0.1 * s)
      .closePath();
  }
  g.moveTo(-0.34 * s, 0.52 * s)
    .quadraticCurveTo(0, 0.26 * s, 0.34 * s, 0.52 * s)
    .quadraticCurveTo(0, 0.4 * s, -0.34 * s, 0.52 * s)
    .closePath();
}

function drawLog(
  pen: Pen,
  x: number,
  y: number,
  angle: number,
  length: number,
  width = LOG_WIDTH,
): void {
  const g = pen.g;
  pen.save().translate(x, y).rotate(angle);
  g.roundRect(-length / 2, -width / 2, length, width, width / 2)
    .fill({ color: WOOD.mid })
    .stroke({ width: 2.4, color: INK });
  g.rect(-length / 2 + width / 2, -width / 2 + 2, length - width, width * 0.3).fill({
    color: WOOD.light,
    alpha: 0.6,
  });
  g.moveTo(-length * 0.34, -width * 0.12)
    .lineTo(-length * 0.06, -width * 0.12)
    .moveTo(-length * 0.18, width * 0.2)
    .lineTo(length * 0.14, width * 0.2)
    .moveTo(length * 0.02, -width * 0.22)
    .lineTo(length * 0.3, -width * 0.22)
    .stroke({ width: 1.3, color: WOOD.dark, alpha: 0.9 });
  for (const end of [-1, 1]) {
    const at = end * (length / 2 - width * 0.22);
    g.ellipse(at, 0, width * 0.22, width * 0.44)
      .fill({ color: WOOD.grain })
      .stroke({ width: 1.6, color: INK });
    g.ellipse(at, 0, width * 0.12, width * 0.26).stroke({ width: 0.9, color: WOOD.ring });
  }
  pen.restore();
}

function drawStick(pen: Pen, player: PlayerState, alpha: number): void {
  const g = pen.g;
  const x = lerp(player.prevX, player.x, alpha);
  const y = lerp(player.prevY, player.y, alpha);
  const angle = Math.atan2(player.aim.y, player.aim.x) + 0.6;
  const reach = player.radius + STICK_LENGTH * 0.3;
  pen
    .save()
    .translate(x + Math.cos(angle) * reach, y + Math.sin(angle) * reach)
    .rotate(angle);
  const half = STICK_LENGTH / 2;
  for (const [width, color] of [
    [8, INK],
    [4.6, WOOD.mid],
  ] as const) {
    g.moveTo(-half, 1.5)
      .lineTo(half, -1.5)
      .moveTo(half * 0.1, 0)
      .lineTo(half * 0.56, -half * 0.4)
      .stroke({ width, color, cap: 'round' });
  }
  g.ellipse(half, -1.5, 1.6, 2.4).fill({ color: WOOD.grain }).stroke({ width: 1, color: INK });
  pen.restore();
}
